/**
 * FolderBrowser (2026-08-20)
 *
 * The ONE folder-drill-down browser for storage scoping, extracted from
 * FolderScopePicker so the onboarding scope sheet and the Integrations picker
 * share a single implementation (owner report 2026-08-20: onboarding offered
 * only a flat top-level list — no way to choose a subfolder).
 *
 * Behavior: breadcrumb trail with per-level listings cached for the session,
 * checkbox per folder (a chosen folder is a master folder — its whole subtree
 * is included; enforcement is subtree-based server-side, storageScope BFS),
 * "Open ›" drills in and subfolders are choosable at ANY depth, a text filter
 * for the current level, root-level "Shared with you" section, error rows
 * with a live retry, and selection chips that span levels so drill-down picks
 * stay visible. Selection state is CONTROLLED by the parent: `onToggle`
 * receives the folder enriched with a human path built from the crumb trail,
 * ready to persist via setStorageScope.
 */

import React from 'react';
import type { StorageScopeFolder } from './ComposioClient';
import { useFolderBrowser, type FolderBrowserProps } from './useFolderBrowser';

// What the browser does is ./useFolderBrowser.ts's (PV14 part 2a); its props are re-exported for this file's importers.
export type { FolderBrowserProps };

export const FolderBrowser: React.FC<FolderBrowserProps> = (props) => {
  const {
    crumbs, browseLoading, browseError, filter, setFilter, filterText, visibleFolders, ownedFolders, sharedFolders,
    selectedList, toggleFolder, enterFolder, goToCrumb, retry,
  } = useFolderBrowser(props);
  const { selected, onToggle } = props;

  const renderFolderRow = (f: StorageScopeFolder) => (
    <div
      key={f.id}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.45rem 0.6rem', borderBottom: '1px solid #F3F4F6',
        background: selected[f.id] ? 'rgba(198, 167, 94, 0.12)' : '#fff',
      }}
    >
      <input
        type="checkbox"
        checked={!!selected[f.id]}
        onChange={() => toggleFolder(f)}
        aria-label={`Use ${f.name}`}
        style={{ cursor: 'pointer', accentColor: 'var(--brand-gold)' }}
      />
      <span aria-hidden="true">{f.shared ? '🤝' : '📁'}</span>
      <span
        onClick={() => toggleFolder(f)}
        style={{ flex: 1, fontSize: '0.85rem', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {f.name}
      </span>
      <button
        type="button"
        onClick={() => enterFolder(f)}
        title={`Open ${f.name} — choose from its subfolders`}
        style={{
          background: 'none', border: '1px solid #E5E7EB', borderRadius: '6px',
          cursor: 'pointer', color: '#6B7280', fontSize: '0.75rem', padding: '0.1rem 0.45rem',
          flexShrink: 0,
        }}
      >
        Open ›
      </button>
    </div>
  );

  return (
    <div>
      {/* Breadcrumb trail */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.5rem', fontSize: '0.78rem' }}>
        {crumbs.map((crumb, i) => (
          <React.Fragment key={crumb.id}>
            {i > 0 && <span style={{ color: '#9CA3AF' }}>›</span>}
            {i < crumbs.length - 1 ? (
              <button
                type="button"
                onClick={() => goToCrumb(i)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-gold-hover)', fontSize: '0.78rem', fontFamily: 'inherit' }}
              >
                {crumb.name}
              </button>
            ) : (
              <span style={{ color: '#4B5563', fontWeight: 600 }}>{crumb.name}</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Text filter for the current level */}
      <input
        type="text"
        placeholder="Filter folders..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.75rem',
          borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '0.85rem',
          outline: 'none', marginBottom: '0.5rem', fontFamily: 'inherit',
        }}
      />

      {/* Folder list */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', marginBottom: '0.6rem', background: '#fff' }}>
        {browseLoading ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>
            Loading folders...
          </div>
        ) : browseError ? (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem' }}>
            <div style={{ color: '#DC2626', marginBottom: '0.5rem' }}>{browseError}</div>
            <button
              type="button"
              onClick={retry}
              style={{
                padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem',
                border: 'none', background: 'var(--brand-gold)', color: 'var(--brand-charcoal)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        ) : visibleFolders.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>
            {filterText
              ? `No folders matching "${filter.trim()}"`
              : crumbs.length > 1
                ? 'No subfolders here'
                : 'No folders found in this account'}
          </div>
        ) : (
          <>
            {ownedFolders.map(renderFolderRow)}
            {sharedFolders.length > 0 && (
              <div style={{
                padding: '0.45rem 0.6rem 0.3rem',
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                textTransform: 'uppercase', color: '#9CA3AF',
                borderBottom: '1px solid #F3F4F6',
                background: '#FAFAF9',
              }}>
                Shared with you
              </div>
            )}
            {sharedFolders.map(renderFolderRow)}
          </>
        )}
      </div>

      {/* Selection summary — spans levels, so drill-down picks stay visible */}
      {selectedList.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: 500 }}>
            Prest0n will use:
          </span>
          {selectedList.map((f) => (
            <span
              key={f.id}
              title={f.path || f.name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                fontSize: '0.75rem', color: '#7a5f1f', background: 'rgba(198, 167, 94, 0.14)',
                padding: '0.15rem 0.5rem', borderRadius: '12px', fontWeight: 500,
              }}
            >
              📁 {f.name}
              <button
                type="button"
                onClick={() => onToggle(f)}
                title={`Remove ${f.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a5f1f', fontSize: '0.8rem', padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#9CA3AF' }}>
          Choose at least one folder — open any folder to pick from its subfolders.
        </div>
      )}
    </div>
  );
};

export default FolderBrowser;
